import { useMemo, useState } from 'react';

import type { IdAdTypeDto, IdPlacementDto } from '@/services/ws-digital/src/main/resources/rest-api';
import { getPaginatedAdTypes } from '@/services/ws-digital/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { NotActive, useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY, DROP_DOWN_ENTITY_LIMIT } from 'src/constants';
import type { Option } from 'src/types';

type UseDigitalAdTypeOptionsParams = {
  placementId: IdPlacementDto | undefined;
};

type UseDigitalAdTypeOptionsReturn = {
  options: Option<IdAdTypeDto>[];
  pending: boolean;
  total: number;
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
};

export const useDigitalAdTypeOptions = ({ placementId }: UseDigitalAdTypeOptionsParams): UseDigitalAdTypeOptionsReturn => {
  const [searchFilter, setSearchFilter] = useState<string>('');

  const request = useRequest({
    apiMethod: getPaginatedAdTypes,
    params: placementId ? { filter: searchFilter, limit: DROP_DOWN_ENTITY_LIMIT, page: 0, adPlacementId: String(placementId) } : NotActive,
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    request.data$,
    (response) => response?.data.items.map((item) => ({ label: item.name ?? '', value: item.id })) ?? [],
  );
  const total = useSubscribe(request.data$, (result) => result?.data?.total ?? 0);
  const pending = useSubscribe(request.pending$);

  return useMemo(() => ({ options, pending, total, searchFilter, setSearchFilter }), [options, pending, total, searchFilter]);
};
